import React, { useState, useEffect, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// Styles
import './App.css';
import './styles/CodeBlocks.css';
import './styles/Models.css';
import './styles/Announcements.css';
import './styles/ModelSystem.css';

// Components
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import BookDemo from './components/BookDemo';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './components/Dashboard';
import ArticlePage from './components/ArticlePage';
import PageRouter from './components/pages/PageRouter';

// Hooks
import { useAppData } from './hooks/useAppData';
import { useUI } from './hooks/useUI';
import { useTicker } from './hooks/useTicker';
import { useLanguage } from './hooks/useLanguage';

// i18n
import './i18n';

function App() {
  const navigate = useNavigate();
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('ampyfin_token');
    return !!token;
  });

  // Custom hooks
  const appData = useAppData();
  const ui = useUI();
  const ticker = useTicker();
  const language = useLanguage();

  // Refs
  const announcementsSectionRef = useRef(null);

  // Handle announcements visibility
  useEffect(() => {
    const observeElements = () => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { threshold: 0.1 });
      
      if (announcementsSectionRef.current) {
        observer.observe(announcementsSectionRef.current);
      }
      
      return observer;
    };
    
    const observer = observeElements();
    
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [appData.currentTab]);

  // Make announcements visible immediately if in viewport
  useEffect(() => {
    if (appData.currentTab === 'home' && announcementsSectionRef.current) {
      const rect = announcementsSectionRef.current.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom >= 0;
      
      if (isInViewport) {
        announcementsSectionRef.current.classList.add('visible');
      }
    }
  }, [appData.currentTab]);

  // Navigation handler
  const handleNavItemClick = (tab) => {
    appData.setCurrentTab(tab);
    // HashRouter automatically handles # prefix
    navigate(tab === 'home' ? '/' : `/${tab}`);
    ui.toggleMenu(false);
    ui.closeAllDropdowns();
  };


  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('ampyfin_token');
    setIsAuthenticated(false);
  };

  // Shared props for PageRouter
  const pageRouterProps = {
    holdings: appData.holdings,
    rankings: appData.rankings,
    trades: appData.trades,
    portfolioPercentage: appData.portfolioPercentage,
    ndaqPercentage: appData.ndaqPercentage,
    spyPercentage: appData.spyPercentage,
    arkkPercentage: appData.arkkPercentage,
    currentLanguage: language.currentLanguage,
    forceRenderKey: language.forceRenderKey,
    toggleBookDemo: ui.toggleBookDemo,
    ticker: ticker.ticker,
    setTicker: ticker.setTicker,
    loading: ticker.loading,
    tickerError: ticker.tickerError,
    tickerResult: ticker.tickerResult,
    handleSubmit: ticker.handleSubmit,
  };

  return (
    <HelmetProvider>
      <div className={`app ${ui.theme}`} key={`app-${language.currentLanguage}`}>
        <Helmet>
          <title>AmpyFin</title>
        </Helmet>
        
        {ui.showBookDemo && <BookDemo onClose={ui.toggleBookDemo} />}
        
        <Navigation
          navVisible={ui.navVisible}
          menuOpen={ui.menuOpen}
          theme={ui.theme}
          isAuthenticated={isAuthenticated}
          aboutDropdownOpen={ui.aboutDropdownOpen}
          dataDropdownOpen={ui.dataDropdownOpen}
          backtestingDropdownOpen={ui.backtestingDropdownOpen}
          toggleMenu={ui.toggleMenu}
          setMenuOpen={(value) => ui.toggleMenu(value)}
          toggleTheme={ui.toggleTheme}
          toggleAboutDropdown={ui.toggleAboutDropdown}
          toggleDataDropdown={ui.toggleDataDropdown}
          toggleBacktestingDropdown={ui.toggleBacktestingDropdown}
          handleNavItemClick={handleNavItemClick}
          handleLogout={handleLogout}
        />
        
        <main className="App-content">
          <Routes>
            {/* Common page routes - all use PageRouter with shared props */}
            <Route path="/" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/how-it-works" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/portfolio" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/rankings" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/trades" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/benchmark" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/mission" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/our-models" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/open-source" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/prag-v3-results" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/hyper-v3-results" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/hyper-v4-results" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/prag-v4-results" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/demo" element={<PageRouter {...pageRouterProps} />} />
            <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/signup" element={<Signup setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/dashboard" element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/article/:articleId" element={<ArticlePage />} />
          </Routes>
        </main>
        
        <Footer />
      </div>
    </HelmetProvider>
  );
}

export default App;
